/**
 * Language + appearance for signed-in users.
 * Device localStorage remains the fast cache; the account preference is the source of truth across browsers.
 */

import {
  type AppearancePreferences,
  loadAppearancePreferences,
  normalizeAppearancePreferences,
  saveAppearancePreferences,
} from './appearanceRuntime';
import { readAccountPreference, writeAccountPreference } from './dashboardWidgets';

export const UI_CHROME_PREF_KEY = 'ui-chrome';

export type WebsiteLanguage = 'en' | 'es';

export type UiChromePreference = {
  language: WebsiteLanguage;
  appearance: AppearancePreferences;
};

export const normalizeUiChromePreference = (
  raw: Partial<UiChromePreference> | null | undefined,
  fallbackLanguage: WebsiteLanguage = 'en',
): UiChromePreference => {
  const language = raw?.language === 'es' || raw?.language === 'en' ? raw.language : fallbackLanguage;
  return {
    language,
    appearance: normalizeAppearancePreferences(raw?.appearance),
  };
};

export const buildUiChromePreference = (
  language: WebsiteLanguage,
  appearance?: AppearancePreferences | null,
): UiChromePreference => ({
  language: language === 'es' ? 'es' : 'en',
  appearance: normalizeAppearancePreferences(appearance || loadAppearancePreferences()),
});

export const loadUiChromeFromAccount = async (
  gatewayBaseUrl: string,
  accessToken: string,
  fallbackLanguage: WebsiteLanguage = 'en',
): Promise<UiChromePreference | null> => {
  const result = await readAccountPreference(gatewayBaseUrl, accessToken, UI_CHROME_PREF_KEY);
  if (!result.ok || result.value == null || typeof result.value !== 'object' || Array.isArray(result.value)) {
    return null;
  }
  return normalizeUiChromePreference(result.value as Partial<UiChromePreference>, fallbackLanguage);
};

export const saveUiChromeToAccount = async (
  gatewayBaseUrl: string,
  accessToken: string,
  chrome: UiChromePreference,
): Promise<void> => {
  const normalized = normalizeUiChromePreference(chrome);
  await writeAccountPreference(gatewayBaseUrl, accessToken, UI_CHROME_PREF_KEY, normalized);
};

/** Apply account chrome locally (language caller updates; appearance writes storage + DOM). */
export const applyUiChromeLocally = (
  chrome: UiChromePreference,
): AppearancePreferences => saveAppearancePreferences(chrome.appearance);
